import React from 'react';
import ReportFields, { TReportField } from './ReportFields';
import Tabs from 'components/Tabs';
import Input from 'components/Input';
import Button from 'components/Button';
import Select from 'components/Select';
import ButtonGroup from 'components/ButtonGroup';
import { IOption } from 'components/Select/Select';
import { ButtonContainer, EditorContainer, EditorTitle } from './styles';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from '../FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import DashboardLinks from './DashboardLinks';
import {
  DashboardLink,
  IReportView,
  ISurveyItem,
  ISurveyViewWithData,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import i18n from '../../i18n';
import { ICompany } from '../../store/companies/companiesTypes';

interface IComponentProps {
  allTags: IOption[];
  canUpdate: boolean;
  editType?: string;
  fields: Array<TReportField>;
  loading: boolean;
  onChangeField: (index: number, field: TReportField) => void;
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeTags: (tags: IOption[]) => void;
  dashboardLinks: DashboardLink[];
  onLinksChanged: (tags: DashboardLink[]) => void;
  onChangeTimeScale: (value: TimeScale) => void;
  onChangeTimeLength: (value: string) => void;
  onPrepareReport: (reportId?: number) => void;
  onRemoveField: (questionId: number, subQuestionId?: string) => void;
  onSaveReport: () => void;
  report?: IReportView;
  reportName: string;
  reportTags: IOption[];
  savingReport: boolean;
  splitOnTime: boolean;
  dashboardWidth: number | undefined;
  dashboardHeight: number | undefined;
  survey?: ISurveyViewWithData;
  surveyReport?: IOpenItem;
  surveyItem?: ISurveyItem;
  timeLength: string;
  timeScale: string;
  totalTime: boolean;
  similarPastYearPeriodLength: number;
  flag: string;
  viewOnlyInMainOwner: boolean;
  company?: ICompany;
  onChangeFlag: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface IComponentStates {
  showCheckPointModal: boolean;
}

export function PeriodSelector(props: {
  timeLength: string;
  onChangeTimeLength: (value: string) => void;
  timeScale: string;
  onChangeTimeScale: (value: TimeScale) => void;
  customApplyOnEnter?: boolean;
}) {
  const dayItems = {
    1: i18n.t('oneDay', { ns: 'survey' }),
    5: i18n.t('fiveDays', { ns: 'survey' }),
    10: i18n.t('tenDays', { ns: 'survey' }),
    30: i18n.t('thirtyDays', { ns: 'survey' }),
  };

  const weekItems = {
    1: i18n.t('oneWeek', { ns: 'survey' }),
    2: i18n.t('twoWeeks', { ns: 'survey' }),
    5: i18n.t('fiveWeeks', { ns: 'survey' }),
  };

  const monthItems = {
    1: i18n.t('oneMonth', { ns: 'survey' }),
    3: i18n.t('threeMonths', { ns: 'survey' }),
    6: i18n.t('sixMonths', { ns: 'survey' }),
    12: i18n.t('twelveMonths', { ns: 'survey' }),
  };

  const seasonItems = {
    1: i18n.t('oneSeason', { ns: 'survey' }),
    2: i18n.t('twoSeason', { ns: 'survey' }),
    4: i18n.t('fourSeason', { ns: 'survey' }),
  };

  const twoSeasonItems = {
    1: i18n.t('oneHalfYear', { ns: 'survey' }),
    2: i18n.t('twoHalfYear', { ns: 'survey' }),
    4: i18n.t('fourHalfYear', { ns: 'survey' }),
  };

  const yearItems = {
    1: i18n.t('oneYear', { ns: 'survey' }),
    2: i18n.t('twoYear', { ns: 'survey' }),
    3: i18n.t('threeYear', { ns: 'survey' }),
  };
  const dayValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={dayItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const weekValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={weekItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const monthValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={monthItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const seasonValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={seasonItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const halfYearValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={twoSeasonItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const yearValues = (
    <ButtonGroup
      onSelect={props.onChangeTimeLength}
      items={yearItems}
      selected={props.timeLength}
      custom="number"
      customApplyOnEnter={props.customApplyOnEnter}
    />
  );
  const tabItems = {
    day: { title: i18n.t('day', { ns: 'survey' }), content: dayValues },
    week: { title: i18n.t('week', { ns: 'survey' }), content: weekValues },
    month: { title: i18n.t('month', { ns: 'survey' }), content: monthValues },
    season: {
      title: i18n.t('season', { ns: 'survey' }),
      content: seasonValues,
    },
    twoSeason: {
      title: i18n.t('halfYear', { ns: 'survey' }),
      content: halfYearValues,
    },
    year: { title: i18n.t('year', { ns: 'survey' }), content: yearValues },
  };

  return (
    <Tabs
      tabItems={tabItems}
      activeTab={props.timeScale}
      onChangeActiveTab={props.onChangeTimeScale as any}
    />
  );
}

class ChartSettingEditor extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    showCheckPointModal: false,
  };

  handlePrepareReport = () => {
    this.props.onPrepareReport(this.props.report && this.props.report.id);
  };

  handleShowCheckPointModal = () => {
    this.setState((prevState) => ({
      showCheckPointModal: !prevState.showCheckPointModal,
    }));
  };

  render() {
    const {
      loading,
      report,
      timeLength,
      onChangeTimeLength,
      timeScale,
      onChangeTimeScale,
      onChangeInput,
      totalTime,
      similarPastYearPeriodLength,
      reportTags,
      reportName,
      survey,
      surveyReport,
      canUpdate,
      onChangeField,
      onRemoveField,
      fields,
      dashboardLinks,
      onLinksChanged,
      surveyItem,
      allTags,
      splitOnTime,
      dashboardWidth,
      dashboardHeight,
      onChangeTags,
      savingReport,
      onSaveReport,
    } = this.props;

    return (
      <>
        <EditorContainer>
          {report || this.props.editType === 'task' ? (
            <></>
          ) : (
            <EditorTitle>
              <span>
                {i18n.t('creatingNewReport', { ns: 'survey' }) as string}
              </span>
            </EditorTitle>
          )}

          <Input
            id="reportName"
            type="text"
            name="reportName"
            value={reportName}
            label={i18n.t('reportTitle', { ns: 'survey' }) as string}
            placeholder={i18n.t('reportTitle', { ns: 'survey' })}
            onChange={onChangeInput}
          />
          <Select
            values={reportTags}
            options={allTags}
            letAddChip={true}
            placeholder={i18n.t('selectReportCategory', { ns: 'survey' })}
            multiChoice={true}
            onChangeValues={onChangeTags}
          />

          {this.props.editType !== 'task' && (
            <>
              <PeriodSelector
                timeLength={timeLength}
                onChangeTimeLength={onChangeTimeLength}
                timeScale={timeScale}
                onChangeTimeScale={onChangeTimeScale as any}
              />
              <p>
                <div
                  style={{
                    display: 'flex',
                    margin: '0.75rem 0.25rem',
                    gap: '0.5rem',
                  }}
                >
                  <Input
                    type="checkbox"
                    name="splitOnTime"
                    id="splitOnTime"
                    checked={splitOnTime}
                    onChange={onChangeInput}
                    label={i18n.t('splitOnTime', { ns: 'survey' }) as string}
                  />
                  <Input
                    disabled={!splitOnTime}
                    type="checkbox"
                    name="totalTime"
                    id="totalTime"
                    checked={totalTime}
                    onChange={onChangeInput}
                    label={i18n.t('totalTime', { ns: 'survey' }) as string}
                  />
                </div>
                {splitOnTime &&
                  !totalTime &&
                  ['season', 'twoSeason', 'month'].includes(timeScale) && (
                    <Input
                      type="number"
                      name="similarPastYearPeriodLength"
                      id="similarPastYearPeriodLength"
                      value={Math.max(similarPastYearPeriodLength, 0)}
                      onChange={onChangeInput}
                      label={
                        i18n.t('showPastYears', { ns: 'survey' }) as string
                      }
                    />
                  )}
              </p>
              <ReportFields
                survey={survey?.data}
                fields={fields}
                surveyItem={surveyItem}
                onChangeField={onChangeField}
                onRemoveField={onRemoveField}
                splitOnTime={splitOnTime}
                loading={loading}
                reportItem={surveyReport}
              />
              <DashboardLinks
                links={dashboardLinks}
                onChangeLinks={onLinksChanged}
                dashboardHeight={dashboardHeight}
                dashboardWidth={dashboardWidth}
                onChangeInput={onChangeInput}
              />
            </>
          )}
        </EditorContainer>
        <Input
          wrapperStyle={{ margin: '0.5rem 0', paddingInlineStart: '0.25rem' }}
          label={i18n.t('isMainReport', { ns: 'survey' }) as string}
          type="checkbox"
          id="isMainReport"
          checked={this.props.flag === 'MainReport'}
          onChange={this.props.onChangeFlag}
        />
        {this.props.company?.streamingConfig?.downStreams && (
          <Input
            wrapperStyle={{ margin: '0.5rem 0', paddingInlineStart: '0.25rem' }}
            label={i18n.t('viewOnlyInMainOwner', { ns: 'survey' }) as string}
            type="checkbox"
            id="viewOnlyInMainOwner"
            name="viewOnlyInMainOwner"
            checked={this.props.viewOnlyInMainOwner}
            onChange={this.props.onChangeInput}
          />
        )}
        {canUpdate &&
          !(surveyReport && report && !!report.surveyId) &&
          report &&
          survey &&
          survey.viewingVersion !== 'init' && (
            <QuestionHintBox>
              <HintIcon type="info" />
              <HintText>
                <span>
                  {
                    i18n.t('updateAppliesToAllVersions', {
                      ns: 'survey',
                    }) as string
                  }
                </span>
              </HintText>
            </QuestionHintBox>
          )}
        <ButtonContainer>
          {this.props.editType !== 'task' && (
            <Button color="blue" onClick={this.handlePrepareReport}>
              {i18n.t('prepareReport', { ns: 'survey' }) as string}
            </Button>
          )}
          <Button
            color="green"
            onClick={onSaveReport}
            loading={savingReport}
            disabled={
              !canUpdate || (surveyReport && report && !!report.surveyId)
            }
          >
            {report
              ? (i18n.t('update', { ns: 'survey' }) as string)
              : (i18n.t('save', { ns: 'survey' }) as string)}
          </Button>
        </ButtonContainer>
      </>
    );
  }
}

export default ChartSettingEditor;
